import { useEffect, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import SwipeableViews from 'react-swipeable-views';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import withStyles from '@mui/styles/withStyles';

import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';

const styles = {
  indicator: {
    backgroundColor: theme.palette.brand,
  },
};

interface WrapperProps {
  theme?: BackgroundThemes;
  height?: string;
  wrapperStyle?: string;
}

const Wrapper = styled.div<WrapperProps>`
  background-color: ${(props) =>
    props.theme === 'light' ? theme.palette.lightPure : 'transparent'};
  width: 100%;
  ${({ height }) =>
    height
      ? `
    height: ${height};
  `
      : ''}
  ${({ wrapperStyle }) => wrapperStyle}
  .MuiAppBar-root {
    background-color: transparent;
  }

  .MuiTabs-root {
    min-height: 32px;
    background-color: transparent;
  }

  .MuiTab-textColorPrimary {
    color: ${(props) =>
      props.theme === 'light'
        ? theme.palette.pureDark
        : theme.palette.lightPure};
    font-weight: 600;
    text-transform: none;
    padding: 0;
    font-size: 16px;
    margin-right: 20px;
    min-width: auto;
    min-height: auto;
  }

  .MuiTab-textColorPrimary.Mui-selected {
    font-weight: 700;
  }

  .MuiTab-root.Mui-disabled {
    color: ${theme.palette.disabledLight};
  }
`;

const SideSpaceWrapper = styled.div`
  padding: 0 1rem;
`;

const Spacer = styled.div`
  height: ${theme.spacing.m1};
`;

interface TabWrapperProps {
  theme?: BackgroundThemes;
}

const TabWrapper = styled.div<TabWrapperProps>`
  height: 100%;
  color: ${(props) =>
    props.theme === 'light' ? theme.palette.pureDark : theme.palette.lightPure};
`;

interface SwipeableViewsWrapperProps {
  height?: string;
}

const SwipeableViewsWrapper = styled.div<SwipeableViewsWrapperProps>`
  ${({ height }) =>
    height
      ? `
    height: ${height};
    .react-swipeable-view-container {
      height: ${height};
    }
  `
      : ''}
`;

interface TabItem {
  label: string;
  component: ReactNode;
  disabled?: boolean;
}

interface FullWidthTabsProps {
  centerTabs?: boolean;
  children?: ReactNode;
  classes: {
    indicator: string;
  };
  contentHeight?: string;
  onChangeTabHandler: (index: number) => void;
  selectedTabIndex?: number;
  tabsToSelect: TabItem[];
  theme?: BackgroundThemes;
  wrapperStyle?: string;
}

const FullWidthTabs = ({
  centerTabs = false,
  children,
  classes,
  contentHeight,
  onChangeTabHandler,
  selectedTabIndex = 0,
  tabsToSelect,
  theme: themeLayout = 'light',
  wrapperStyle,
}: FullWidthTabsProps) => {
  const muiTheme = useTheme();
  const [value, setValue] = useState(selectedTabIndex);

  useEffect(() => {
    setValue(selectedTabIndex);
  }, [selectedTabIndex]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    onChangeTabHandler(newValue);
  };

  const handleChangeIndex = (index: number) => {
    onChangeTabHandler(index);
  };

  const swipeableHeight = contentHeight
    ? {
        height: contentHeight,
        '.reactSwipeableViewContainer': {
          height: contentHeight,
        },
      }
    : undefined;

  return (
    <Wrapper
      theme={themeLayout}
      height={contentHeight}
      wrapperStyle={wrapperStyle}
    >
      <SideSpaceWrapper>
        <AppBar position="static" color="inherit" elevation={0}>
          <Tabs
            aria-label="full-width-tabs"
            centered={centerTabs}
            classes={{
              indicator: classes.indicator,
            }}
            onChange={handleChange}
            textColor="primary"
            value={value}
          >
            {tabsToSelect.map((tab, index) => (
              <Tab
                disabled={tab.disabled}
                label={tab.label}
                key={tab.label}
                id={`full-width-tab-${index}`}
              />
            ))}
          </Tabs>
        </AppBar>
        <Spacer />
        {children && (
          <>
            {children}
            <Spacer />
          </>
        )}
      </SideSpaceWrapper>
      <SwipeableViewsWrapper height={contentHeight}>
        <SwipeableViews
          axis={muiTheme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={value}
          onChangeIndex={handleChangeIndex}
          style={swipeableHeight}
        >
          {tabsToSelect.map((tab) => (
            <TabWrapper key={tab.label} theme={themeLayout}>
              {tab.component}
            </TabWrapper>
          ))}
        </SwipeableViews>
      </SwipeableViewsWrapper>
    </Wrapper>
  );
};

export default withStyles(styles)(FullWidthTabs);
export type { FullWidthTabsProps, TabItem };
